/**
 * StaffPreviewBanner
 *
 * A persistent banner that renders when a Front Office / League Admin
 * user is browsing the *member-facing* side of the app. Makes it
 * unmistakably clear they're viewing the member experience through
 * permission-based staff access, NOT as a paid Pro Member.
 *
 * Visibility rules:
 *   - Hidden if the viewer is not an admin role (front_office_staff /
 *     team_admin) — normal Pro Members never see this.
 *   - Hidden on the homepage `/`.
 *   - Hidden on League Admin (those pages use a different layout, so
 *     the banner is never even mounted there — safe by construction).
 *   - Hidden on auth/legal pages (login, register, terms, privacy, etc.)
 *
 * Compliance:
 *   - This is a permission-based override, not a subscription. The
 *     banner explicitly tells the user they are NOT a Pro Member.
 *   - Backend `require_pro_member` already bypasses ADMIN_ROLES
 *     (deps.py), so this UI banner only confirms what the API allows.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Eye, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const HIDDEN_PATH_PREFIXES = [
  '/league-admin', // never on League Admin (different Layout anyway)
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/privacy',
  '/privacy-policy',
  '/terms',
  '/disclaimer',
  '/community-guidelines',
  '/contact',
  '/become-a-member',
  '/deactivated',
];

const HIDDEN_EXACT_PATHS = ['/'];

const StaffPreviewBanner = () => {
  const { user, isAdminRole } = useAuth();
  const location = useLocation();

  // Only show for admin roles (FO staff + team_admin). Normal Pro
  // members must never see this.
  if (!user || !isAdminRole) return null;

  const path = location.pathname;
  if (HIDDEN_EXACT_PATHS.includes(path)) return null;
  if (HIDDEN_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    return null;
  }

  return (
    <div
      data-testid="staff-preview-banner"
      className="relative z-30 bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-transparent border-b border-amber-500/30 backdrop-blur-sm"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="shrink-0 w-7 h-7 flex items-center justify-center bg-amber-500/20 border border-amber-500/40">
            <Eye className="w-3.5 h-3.5 text-amber-300" />
          </div>
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300 flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" />
              Staff Preview Mode
            </div>
            <div className="text-[12px] text-amber-100/85 leading-tight truncate">
              Viewing Member Features as{' '}
              <span className="font-semibold">
                {user.role === 'team_admin' ? 'Team Admin' : 'Front Office Staff'}
              </span>
              <span className="hidden sm:inline">
                {' '}
                · permission-based access · not a paid Pro Member
              </span>
            </div>
          </div>
        </div>

        <Link
          to="/league-admin"
          data-testid="staff-preview-back-btn"
          className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-amber-200 hover:text-amber-100 border border-amber-500/40 hover:border-amber-400/60 bg-amber-500/10 hover:bg-amber-500/20 px-3 py-1.5 transition-colors"
        >
          <ArrowLeft className="w-3 h-3" />
          League Admin
        </Link>
      </div>
    </div>
  );
};

export default StaffPreviewBanner;
